import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { isAuthRequired } from '@/lib/featureFlags';
import type { Permission } from '@/schemas/auth/auth.schemas';
import { hasEffectivePermission } from '@/lib/permissions';
import { UnauthorizedPage } from './UnauthorizedPage';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Single permission required to access this route */
  requiredPermission?: Permission;
  /** Multiple permissions to check */
  requiredPermissions?: Permission[];
  /** When true, ALL requiredPermissions must be present (default: false — any match) */
  requireAll?: boolean;
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}: ProtectedRouteProps): React.JSX.Element {
  const { isAuthenticated, isLoading, currentTenantAccess } = useAuth();
  const location = useLocation();

  // If authentication is not required, allow access to all users
  if (!isAuthRequired()) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If authentication is required and user is not authenticated, redirect to login
  // isAuthenticated checks both user AND tenant
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission requirements after confirming authentication
  if (requiredPermission) {
    const permitted =
      !!currentTenantAccess &&
      hasEffectivePermission(currentTenantAccess.permissions, requiredPermission);
    if (!permitted) {
      return <UnauthorizedPage />;
    }
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const perms = currentTenantAccess?.permissions ?? [];
    const permitted = requireAll
      ? requiredPermissions.every(p => hasEffectivePermission(perms, p))
      : requiredPermissions.some(p => hasEffectivePermission(perms, p));
    if (!permitted) {
      return <UnauthorizedPage />;
    }
  }

  return <>{children}</>;
}
